import React, { useContext } from "react"
import styled from "@emotion/styled"
import Section from "src/components/Section"
import Image from "src/components/Image"
import Grid from "src/components/Grid"
import Modal from "src/components/Modal"
import Link from "src/components/Link"
import SanityRichText from "src/components/SanityRichText"
import TeamSlider from "./TeamSlider"
import { AppContext } from "src/state/AppState"
import { MdKeyboardArrowRight } from "react-icons/md"
import { typography, colors, animations, globals, mq, util } from "src/styles"
import { getSlugLink } from "src/utils/format"

const Wrapper = styled(Section)`
  ${({ vertical }) =>
    `
    ${globals.verticalSpacing("padding-top", vertical)}
    ${globals.verticalSpacing("padding-bottom", vertical)}
    `}
  ${({ horizontal }) =>
    `
    ${globals.verticalSpacing("padding-left", horizontal)}
    ${globals.verticalSpacing("padding-right", horizontal)}
  `}
  ${mq.mediumAndBelow} {
    ${({ hideMobile }) => hideMobile && "display: none"}
  }
  ${mq.largeAndUp} {
    ${({ hideDesktop }) => hideDesktop && "display: none"}
  }
`

const IntroWrap = styled.div`
  ${globals.verticalSpacing("margin-bottom", 0.5)}
  h3 {
    margin: 0;
  }
`

const ThumbnailInfo = styled.div`
  p {
    transition: color ${animations.mediumSpeed} ease-in-out;
  }
  p.name {
    margin: .75em 0 0;
    ${typography.body}
    color: ${colors.textColor};
    line-height: 1.4em;
    ${mq.mediumAndBelow} {
      ${typography.bodyMedium}
      line-height: 1.4em;
    }
  }
  p.title {
    margin: 0;
    ${typography.bodySmall}
    line-height: 1.4em;
  }
`

const MemberModal = styled(Modal)`
  width: ${(100 / 14) * 12}%;
  ${mq.smallAndBelow} {
    width: calc(100% - var(--site-margins) * 2);
  }
  ${util.responsiveStyles("max-width", 1200, 800, 750, 700)}
  .close-button {
    ${mq.smallAndBelow} {
      background: var(--text-color);
      color: var(--bg-color);
      top: 14px;
      right: 14px;
    }
  }
`

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  ${util.responsiveStyles("padding", 40, 30, 24, 20)}
`

const ModalHeadshot = styled(Image)`
  height: 100%;
  border-radius: var(--card-border-radius) var(--card-border-radius) 0 0;
  img {
    border-radius: var(--card-border-radius) var(--card-border-radius) 0 0;
  }
  ${mq.mediumAndUp} {
    border-radius: var(--card-border-radius) 0 0 var(--card-border-radius);
    img {
      border-radius: var(--card-border-radius) 0 0 var(--card-border-radius);
    }
  }
`

const ModalTitle = styled.div`
  h4 {
    margin: -0.15em 0 0;
  }
  p {
    margin: 0 0 1.5em;
  }
`

const MemberLinks = styled.ul`
  list-style: none;
  ${typography.bodySmall}
  margin-top: 1.75em;
  padding: 0;
  li {
    margin: 0;
  }
  a {
    display: inline-flex;
    align-items: center;
    text-transform: capitalize;
    color: var(--main-color);
    position: relative;
    line-height: 1.7em;
    font-weight: ${typography.bold};
    svg {
      margin-right: -5px;
    }
    &:after {
      content: "";
      display: block;
      position: absolute;
      top: 85%;
      left: 0;
      width: 100%;
      height: 1px;
      background: currentcolor;
      transform: scaleX(0);
      transform-origin: right center;
      transition: transform ${animations.mediumSpeed} ease-in-out;
    }
    &:hover {
      &:after {
        transform-origin: left center;
        transform: scaleX(1);
      }
    }
  }
`

const MemberBio = styled(SanityRichText)`
  p {
    ${typography.bodySmall}
  }
`

const ThumbnailImage = styled(Image)`
  &:after {
    content: "";
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: black;
    opacity: 0;
    transition: opacity ${animations.mediumSpeed} ease-in-out;
    border-radius: var(--card-border-radius);
  }
`

const ThumbnailItem = styled.div`
  display: block;
  ${({ hasBio }) =>
    hasBio
      ? `
    &:hover {
      ${ThumbnailInfo} {
        p.name {
          color: var(--main-color);
        }
        p.title {
          color: var(--text-color);
        }
      }
      ${ThumbnailImage} {
        &:after {
          opacity: .2;
        }
      }
    }
  `
      : ""}
`

const TeamMember = ({ member }) => {
  const { toggleModal } = useContext(AppContext)
  const onClick = () => {
    if (member?._rawBio) {
      toggleModal(member._key + "-team-modal")
    } else {
      return false
    }
  }
  return (
    <ThumbnailItem
      onClick={onClick}
      hasBio={member?._rawBio}
      as={member?._rawBio ? "a" : "div"}
    >
      {member?.headshot?.asset?.gatsbyImageData && (
        <ThumbnailImage
          rounded
          image={member.headshot.asset.gatsbyImageData}
          ratio={1}
        />
      )}
      <ThumbnailInfo>
        <p className="name">{member.name}</p>
        <p className="title">{member.jobTitle}</p>
      </ThumbnailInfo>
    </ThumbnailItem>
  )
}

const TeamGrid = ({
  className,
  nextTheme,
  prevTheme,
  theme,
  spacing,
  isFirstSection,
  headingSize,
  headline,
  teamMembers,
  type,
  id,
  hideMobile,
  hideDesktop,
}) => {
  const { toggleModal } = useContext(AppContext)

  return (
    <>
      {type !== "slider" ? (
        <Wrapper
          className={className}
          prevTheme={prevTheme}
          setTheme={theme}
          nextTheme={nextTheme}
          isFirstSection={isFirstSection}
          id={id}
          vertical={spacing?.vertical}
          horizontal={spacing?.horizontal}
          hideMobile={hideMobile}
          hideDesktop={hideDesktop}
        >
          <Grid small="m [1] m" medium="1 [12] 1">
            <IntroWrap>
              <h3 class={headingSize ? headingSize : "h3"}>
                {headline || "Our Team"}
              </h3>
            </IntroWrap>
          </Grid>
          <Grid small="m [1] m" medium="1 [12] 1">
            <div>
              {teamMembers?.length > 0 && (
                <Grid
                  small="[1] [1]"
                  medium="[1] [1] [1]"
                  large="[1] [1] [1] [1]"
                  extraLarge="[1] [1] [1] [1] [1]"
                  colGap={["20px", "20px", "30px"]}
                  rowGap={["36px", "50px", "60px"]}
                >
                  {teamMembers.map(member => (
                    <div>
                      <TeamMember member={member} key={member._key} />
                    </div>
                  ))}
                </Grid>
              )}
            </div>
          </Grid>
        </Wrapper>
      ) : (
        <TeamSlider
          teamMembers={teamMembers}
          toggleModal={toggleModal}
          headline={headline}
          headingSize={headingSize}
          prevTheme={prevTheme}
          theme={theme}
          nextTheme={nextTheme}
          isFirstSection={isFirstSection}
          id={id}
          vertical={spacing?.vertical}
          horizontal={spacing?.horizontal}
        />
      )}

      {teamMembers.map(member => (
        <MemberModal id={member._key + "-team-modal"}>
          <Grid
            small="[1]"
            medium="[3] [5]"
            extraLarge="[4] [5]"
            vAlign="stretch"
          >
            <div style={{ height: "100%" }}>
              <ModalHeadshot
                image={member?.headshot?.asset?.gatsbyImageData}
                ratio={1}
              />
            </div>
            <ModalContent>
              <div>
                <ModalTitle>
                  {member?.name && <h4>{member.name}</h4>}
                  {member?.jobTitle && <p>{member.jobTitle}</p>}
                </ModalTitle>
                <MemberBio text={member?._rawBio} />
              </div>
              <MemberLinks>
                {member.links.map(link => {
                  if (link._type) {
                    const url = link.externalLink
                      ? link.externalLink
                      : getSlugLink(link.link, false, link.linkSection)
                    const newTab = link.newTab

                    return (
                      <li>
                        <Link
                          to={url}
                          external={link.external || link.newTab}
                          target={newTab ? "_blank" : ""}
                        >
                          {link.title}
                          <MdKeyboardArrowRight size={18} />
                        </Link>
                      </li>
                    )
                  } else {
                    let linkUrl = link.url
                    if (link.icon === "email") {
                      linkUrl = link.email
                    }

                    return (
                      <li>
                        <Link to={linkUrl} external target="_blank">
                          {link?.icon}
                          <MdKeyboardArrowRight size={18} />
                        </Link>
                      </li>
                    )
                  }
                })}
              </MemberLinks>
            </ModalContent>
          </Grid>
        </MemberModal>
      ))}
    </>
  )
}

export default TeamGrid
